import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicShell, useSeo } from "../glossary/PublicShell";
import { Spinner } from "@/components/shared";
import {
  MapPin, Video, Building2, Star, ArrowRight, ChevronRight, Sparkles,
  ShieldAlert, Rocket, ChevronDown,
} from "lucide-react";

export const CATEGORY_SLUGS = new Set([
  "speech-therapy", "occupational-therapy", "psychotherapy", "behaviour-therapy-aba",
  "psychology-assessment", "social-work", "parent-coaching", "respite-support",
  "educational-support-tutoring", "life-skills-support", "social-skills-programs",
  "feeding-support", "sleep-support", "recreation-programs", "assistive-technology",
  "other-services",
]);

const SITE = "https://kidpath.ca";

function ProviderCard({ p }) {
  const d = p.delivery || {};
  const formats = [d.virtual && "Virtual", d.in_person && "In person", d.hybrid && "Hybrid"].filter(Boolean);
  return (
    <Link to={`/providers/${p.slug}`} className="kid-card p-6 block hover:shadow-md transition-shadow group" data-testid="provider-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-kid-skytint grid place-items-center shrink-0 overflow-hidden">
          {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={22} className="text-kid-teal" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold text-kid-navy group-hover:text-kid-teal transition-colors">{p.business_name}</h3>
          {p.headline && <p className="text-sm text-kid-muted mt-0.5 line-clamp-2">{p.headline}</p>}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-kid-muted">
            {p.rating_count > 0 && (
              <span className="inline-flex items-center gap-1 font-semibold text-kid-navy">
                <Star size={12} className="text-kid-sun fill-kid-sun" /> {p.rating_avg?.toFixed(1)} <span className="font-normal text-kid-muted">({p.rating_count})</span>
              </span>
            )}
            {p.primary_city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.primary_city}</span>}
            {formats.length > 0 && <span className="inline-flex items-center gap-1"><Video size={12} /> {formats.join(" · ")}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="kid-card overflow-hidden" data-testid="seo-faq-item">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 p-5 text-left" data-testid="seo-faq-toggle">
        <span className="font-poppins font-semibold text-kid-navy">{q}</span>
        <ChevronDown size={18} className={`text-kid-muted shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 -mt-1 text-kid-muted text-sm leading-relaxed">{a}</div>}
    </div>
  );
}

export default function CategoryLanding() {
  const params = useParams();
  const category = params.category || params.slug;
  const city = params.city || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = city ? `/marketplace/seo/${category}/${city}` : `/marketplace/seo/${category}`;
    api.get(url).then((r) => setData(r.data)).catch(() => setData(false)).finally(() => setLoading(false));
  }, [category, city]);

  const path = city ? `/providers/${category}/${city}` : `/providers/${category}`;
  const crumbs = [
    { name: "Home", url: `${SITE}/` },
    { name: "Service Finder", url: `${SITE}/providers` },
    { name: data?.name || category, url: `${SITE}/providers/${category}` },
  ];
  if (city && data?.city) crumbs.push({ name: data.city.name, url: `${SITE}${path}` });

  const jsonLd = data ? [
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: c.url })),
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: (data.faqs || []).map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: data.h1, description: data.meta_description, url: `${SITE}${path}`,
    },
  ] : null;

  useSeo(data ? { title: data.title, description: data.meta_description, jsonLd } : {});

  if (loading) return <PublicShell><div className="py-24"><Spinner /></div></PublicShell>;
  if (!data) return (
    <PublicShell>
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-24 text-center">
        <h1 className="font-display text-2xl font-extrabold text-kid-navy">Page not found</h1>
        <Link to="/providers" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold mt-4">Back to Service Finder</Link>
      </section>
    </PublicShell>
  );

  const hasProviders = data.providers?.length > 0;

  return (
    <PublicShell>
      {/* Breadcrumbs */}
      <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] pt-8">
        <nav className="flex items-center flex-wrap gap-1 text-xs text-kid-muted" data-testid="seo-breadcrumbs">
          <Link to="/providers" className="hover:text-kid-teal">Service Finder</Link>
          <ChevronRight size={12} />
          <Link to={`/providers/${category}`} className="hover:text-kid-teal">{data.name}</Link>
          {city && data.city && (<><ChevronRight size={12} /><span className="text-kid-navy font-medium">{data.city.name}</span></>)}
        </nav>
      </div>

      {/* Hero */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] pt-6 pb-10" data-testid="seo-category-page">
        <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3"><Sparkles size={16} /> Ontario Service Finder</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-kid-navy leading-[1.06] max-w-3xl" data-testid="seo-h1">{data.h1}</h1>
        <p className="text-kid-muted mt-4 text-base sm:text-lg leading-relaxed max-w-2xl">{data.intro}</p>
      </section>

      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] pb-16">
        {/* Providers or coming soon */}
        {hasProviders ? (
          <>
            <p className="text-sm text-kid-muted mb-4" data-testid="seo-provider-count">
              {data.count} {data.name.toLowerCase()} provider{data.count === 1 ? "" : "s"} {data.loc_phrase}
            </p>
            <div className="grid md:grid-cols-2 gap-5">{data.providers.map((p) => <ProviderCard key={p.id} p={p} />)}</div>
          </>
        ) : (
          <div className="rounded-3xl bg-kid-navy text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="seo-coming-soon">
            <div>
              <p className="inline-flex items-center gap-2 bg-kid-teal/20 text-kid-teal font-semibold text-sm px-4 py-1.5 rounded-full mb-3"><Rocket size={15} /> Coming soon</p>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-white">We're adding {data.name.toLowerCase()} providers {data.loc_phrase}</h2>
              <p className="text-white/80 mt-1.5 text-sm sm:text-base">Are you a provider? List your practice free and be seen first by families at launch.</p>
            </div>
            <Link to="/provider/register" className="inline-flex items-center gap-2 bg-kid-teal text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 whitespace-nowrap shrink-0" data-testid="seo-provider-cta">
              List your practice <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* Browse by city (category-only page) */}
        {!city && (
          <div className="mt-14" data-testid="seo-city-links">
            <h2 className="font-display text-xl font-extrabold text-kid-navy mb-4">{data.name} by city in Ontario</h2>
            <div className="flex flex-wrap gap-2">
              {data.cities.map((c) => (
                <Link key={c.slug} to={`/providers/${category}/${c.slug}`} className="text-sm bg-kid-mist text-kid-navy/80 hover:bg-kid-skytint hover:text-kid-navy px-3 py-1.5 rounded-full transition-colors" data-testid="seo-city-link">
                  {data.name} in {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related services */}
        <div className="mt-14" data-testid="seo-related-links">
          <h2 className="font-display text-xl font-extrabold text-kid-navy mb-4">Other services{city && data.city ? ` in ${data.city.name}` : ""}</h2>
          <div className="flex flex-wrap gap-2">
            {data.related_categories.map((rc) => (
              <Link key={rc.slug} to={city ? `/providers/${rc.slug}/${city}` : `/providers/${rc.slug}`} className="text-sm bg-kid-mist text-kid-navy/80 hover:bg-kid-skytint hover:text-kid-navy px-3 py-1.5 rounded-full transition-colors" data-testid="seo-related-link">
                {rc.name}
              </Link>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="mt-14" data-testid="seo-faqs">
          <h2 className="font-display text-2xl font-extrabold text-kid-navy mb-4">Frequently asked questions</h2>
          <div className="space-y-3 max-w-3xl">{data.faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} />)}</div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 flex items-start gap-2 text-xs text-kid-muted border-t border-kid-line pt-6" data-testid="seo-disclaimer">
          <ShieldAlert size={14} className="shrink-0 mt-0.5" /> <span>{data.disclaimer}</span>
        </div>
      </section>
    </PublicShell>
  );
}
